import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { getStaticUrl } from "js/config";
import { WorkspacePlanTier } from "common/interfaces";
import FlowProgress from "js/react/views/AiOnboardingFlow/components/FlowProgress/FlowProgress";
import { AiOnboardingSteps } from "js/react/views/AiOnboardingFlow/AiOnboardingFlowConstants";

const OverlayContainer = styled.div`
    height: 100vh;
    height: 100dvh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 0;
    background: #FFF;
    z-index: 1000;
    box-shadow: none;
    overflow: hidden;

    @media (min-width: 768px) {
        height: fit-content;
        width: 45vw;
        border-radius: 4px;
        box-shadow: 0px 9px 37px 0px rgba(0, 0, 0, 0.2);
    }
`;

const CheckoutContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 20px 30px;
    flex: 1;
    align-self: stretch;
    width: 100%;
    overflow-y: auto;
    min-height: 0;

    @media (min-width: 768px) {
        gap: 30px;
        padding: 40px 50px 20px;
    }
`;

const HeaderTitle = styled.h1`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 1.2;
    color: #444444;
    margin: 0;
    text-align: center;
    padding: 0 10px;

    @media (min-width: 768px) {
        font-size: 24px;
        white-space: nowrap;
        padding: 0;
    }
`;

const PlansContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    justify-content: flex-start;
    width: 100%;

    @media (min-width: 768px) {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 30px;
        align-items: flex-start;
        justify-content: center;
    }
`;

const FlowProgressContainer = styled.div`
    width: 100%;
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    background: #FFF;
    z-index: 10;
    padding:0 20px;

    @media (min-width: 768px) {
        width: calc(100% + 50px);
    }
`;

const PlanCard = styled.div<{ isPro?: boolean }>`
    display: flex;
    padding: 15px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    max-width: 310px;
    height: auto;
    min-height: 368px;
    border-radius: 2px;
    border: 1px solid #dae9f0;
    background: #f7fafc;
    box-sizing: border-box;
    position: relative;
    flex-shrink: 0;

    @media (min-width: 768px) {
        width: 240px;
        height: 368px;
        min-height: 0;
    }
`;

const PlanName = styled.div`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
    color: #333333;
    width: min-content;
    min-width: 100%;
    flex-shrink: 0;
`;

const PriceContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    flex-shrink: 0;
`;

const Price = styled.div<{ isPro?: boolean }>`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 26px;
    line-height: 1.2;
    color: ${props => props.isPro ? "#11a9e2" : "#168ef9"};
    text-transform: uppercase;
    white-space: nowrap;
    text-align: center;
    flex-shrink: 0;
`;

const PriceDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
    justify-content: flex-start;
    width: 81px;
    flex-shrink: 0;
    color: #666666;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
`;

const PricePerUser = styled.div`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
`;

const BillingPeriod = styled.div`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 400;
`;

const TryButton = styled.button<{ isPro?: boolean }>`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 6px 16px;
    background: ${props => props.isPro ? "#11a9e2" : "#168ef9"};
    border-radius: 1px;
    border: none;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        opacity: 0.9;
    }
`;

const ButtonText = styled.span`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    text-align: center;
`;

const Spacer = styled.div`
    width: 10px;
    height: 5px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    width: 100%;
    height: 0;
    position: relative;
    flex-shrink: 0;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: -0.5px;
        bottom: -0.5px;
        border-top: 1px solid #eeeeee;
    }
`;

const FeatureItem = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    flex-shrink: 0;
`;

const CheckIconWrapper = styled.div<{ isTeam?: boolean }>`
    width: 14px;
    height: 14px;
    position: relative;
    flex-shrink: 0;

    ${props => props.isTeam && `
        img {
            filter: brightness(0) saturate(100%) invert(44%) sepia(93%) saturate(2067%) hue-rotate(196deg) brightness(99%) contrast(97%);
        }
    `}
`;

const FeatureText = styled.div`
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 11px;
    line-height: 1.2;
    color: #444444;
    flex: 1 0 0;
    min-height: 1px;
    min-width: 1px;
`;

const CheckIcon = getStaticUrl("/images/billing/check-circle.svg");

export default function ChoosePlan({ handlePlanSelected }: { handlePlanSelected: (plan: WorkspacePlanTier) => void }) {
    const { t } = useTranslation();

    const proFeatures = [
        "Generate slides and presentations with AI",
        "Hundreds of Smart Slide templates",
        "Auto-formatting layouts",
        "Stunning data visualization",
        "Customize themes and styles",
        "Image and icon libraries"
    ];

    const teamFeatures = [
        "Everything in Pro",
        "Collaborative workspace",
        "Viewer analytics and tracking",
        "Centralized slide and template library",
        "Shared asset library",
        "SSO (Single Sign-on)",
        "Priority support"
    ];

    return (
        <OverlayContainer>
            <CheckoutContentContainer>
                <HeaderTitle>{t("Choose the plan that's right for you")}</HeaderTitle>

                <PlansContainer>
                    <PlanCard isPro>
                        <PlanName>{t("Pro")}</PlanName>
                        <PriceContainer>
                            <Price isPro>{t("$12")}</Price>
                            <PriceDetails>
                                <PricePerUser>{t("per user/mo")}</PricePerUser>
                                <BillingPeriod>{t("billed annually")}</BillingPeriod>
                            </PriceDetails>
                        </PriceContainer>
                        <TryButton isPro onClick={() => handlePlanSelected(WorkspacePlanTier.PRO)} data-testid="choose-pro">
                            <ButtonText>{t("Try it free")}</ButtonText>
                        </TryButton>
                        <Spacer />
                        <Divider />
                        {proFeatures.map((feature, index) => (
                            <React.Fragment key={index}>
                                <FeatureItem>
                                    <CheckIconWrapper>
                                        <img src={CheckIcon} alt="" style={{ width: "14px", height: "14px" }} />
                                    </CheckIconWrapper>
                                    <FeatureText>{t(feature)}</FeatureText>
                                </FeatureItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </PlanCard>

                    <PlanCard>
                        <PlanName>{t("Team")}</PlanName>
                        <PriceContainer>
                            <Price>{t("$40")}</Price>
                            <PriceDetails>
                                <PricePerUser>{t("per user/mo")}</PricePerUser>
                                <BillingPeriod>{t("billed annually")}</BillingPeriod>
                            </PriceDetails>
                        </PriceContainer>
                        <TryButton onClick={() => handlePlanSelected(WorkspacePlanTier.TEAM)} data-testid="choose-team">
                            <ButtonText>{t("Try it free")}</ButtonText>
                        </TryButton>
                        <Spacer />
                        <Divider />
                        {teamFeatures.map((feature, index) => (
                            <React.Fragment key={index}>
                                <FeatureItem>
                                    <CheckIconWrapper isTeam>
                                        <img src={CheckIcon} alt="" style={{ width: "14px", height: "14px" }} />
                                    </CheckIconWrapper>
                                    <FeatureText>{t(feature)}</FeatureText>
                                </FeatureItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </PlanCard>
                </PlansContainer>
            </CheckoutContentContainer>

            <FlowProgressContainer>
                <FlowProgress step={AiOnboardingSteps.CHECKOUT_OVERLAY} />
            </FlowProgressContainer>
        </OverlayContainer>
    );
}
